// src/pages/CheckInPage.tsx
import React, { useContext, useState, useEffect } from "react";
import {
    Box, TextField, Button, Typography, Grid, CircularProgress, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from "react-i18next";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { AppContext } from "../context/AppContext";
import { loginAndFetchInitialData } from "../services/LoginService";
import { getBrandLogo } from "../styles/branding";
import BrandingThemeToggle from "../components/BrandingThemeToggle";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { SUPPORTED_LOCALES } from "../i18n";

const CheckInPage: React.FC = () => {
    const { colors: BrandColors, isDark: darkUi } = useBrandAppearance();

    const [lastName, setLastName] = useState("");
    const [confirmationNumber, setConfirmationNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { navigateWithLang, changeLanguage } = useNavigateWithLang();

    const { t } = useTranslation("check_in");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { dispatch } = useContext(AppContext)!;

    const [searchParams] = useSearchParams();

    const handleStartCheckIn = async (ln?: string, cn?: string) => {
        const lnVal = (ln ?? lastName).trim();
        const cnVal = (cn ?? confirmationNumber).trim();

        setLoading(true);
        setError(null);
        try {
            await loginAndFetchInitialData(lnVal, cnVal, dispatch);
            navigateWithLang("/guest-info");
        } catch (err: any) {
            console.error("Login or reservation fetch failed", err);

            // Try to read a message coming from the API (Axios-style errors)
            const serverMessage: string =
                err?.response?.data?.error?.message ||
                err?.response?.data?.message ||
                err?.message ||
                "";

            const status = err?.response?.status;
            const exceptionClass = String(err?.response?.data?.exception ?? "");

            // If backend says status not allowed (e.g., "CHANGED"), show custom text
            if (/reservation status not allowed/i.test(serverMessage)) {
                setError(
                    t("onlyReservedAllowed", {
                        defaultValue: "Only Reserved bookings are allowed",
                    })
                );
            } else if (
                status === 403 &&
                exceptionClass.includes("LastNameMismatchException")
            ) {
                setError(
                    t("lastNameMismatch", {
                        defaultValue:
                            "The last name does not match this reservation. Please check and try again.",
                    })
                );
            } else if (status === 400 && serverMessage) {
                setError(serverMessage);
            } else {
                setError(
                    t("authFailed", {
                        defaultValue: "Authentication failed. Please try again.",
                    })
                );
            }
        }
        finally {
            setLoading(false);
        }
    };

    // Prefill ONCE from query params (no re-running on edits)
    useEffect(() => {
        const lastNameParam = (searchParams.get("lastName") || "").trim();
        const confirmationParam = (searchParams.get("confirmationNo") || "").trim();
        const langParam = searchParams.get("lang");
        const shouldAuto = (searchParams.get("auto") || "").toLowerCase() === "true";

        if (lastNameParam) setLastName(lastNameParam);
        if (confirmationParam) setConfirmationNumber(confirmationParam);

        if (langParam && (SUPPORTED_LOCALES as readonly string[]).includes(langParam)) {
            changeLanguage(langParam);
        }

        // Auto-submit immediately if requested and both fields are present
        if (shouldAuto && lastNameParam && confirmationParam) {
            // Call with the param values directly to avoid any state timing issues
            handleStartCheckIn(lastNameParam, confirmationParam);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // empty deps → run only once on mount

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                height: "100dvh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "center",
                backgroundColor: "var(--oc-page-bg)",
                color: "var(--oc-text)",
                textAlign: "center",
                px: 2,
                py: { xs: 3, sm: 5 },
                position: "relative",
                overflowY: "auto",
                overflowX: "hidden",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 1,
                }}
            >
                <BrandingThemeToggle variant={darkUi ? "onDark" : "onLight"} />
            </Box>
            {/* Logo & divider in normal flow */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: { xs: 3, sm: 6 },
                    mt: { xs: 3, sm: 5 },
                    px: 2,
                }}
            >
                <Box
                    component="img"
                    src={getBrandLogo(darkUi ? "dark" : "light")}
                    alt="Hotel Logo"
                    sx={{
                        width: { xs: "200px", sm: "260px" },
                        maxWidth: "60vw",
                        height: "auto",
                    }}
                />
            </Box>

            <Typography variant="h3" sx={{ fontFamily: "'Playfair Display', serif", fontWeight: "bold", mb: 7, fontSize: { xs: "2rem", sm: "3rem" }, lineHeight: 1.1, color: "var(--oc-text)" }}>
                Fast Check-In
            </Typography>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Typography variant="body1" sx={{ color: "var(--oc-text)", opacity: darkUi ? 0.92 : 1, mt: 2, maxWidth: 500, mb: 6 }}>
                {t("subheader")}
            </Typography>

            <Grid container spacing={2} sx={{
                maxWidth: 600,
                mb: 4,
                px: isMobile ? 0 : 2,
                flexDirection: { xs: "column", sm: "row" },
            }}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t("lastNameInputLabel")}
                        variant="filled"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        sx={{
                            "& .MuiFilledInput-root": {
                                borderRadius: "4px",
                                boxShadow: darkUi ? "0 4px 8px rgba(0,0,0,0.35)" : "0px 4px 6px rgba(0,0,0,0.12)",
                                backgroundColor: darkUi ? "var(--oc-paper-bg)" : "rgba(0,0,0,0.06)",
                            },
                            "& .MuiFilledInput-input": { color: "var(--oc-text)" },
                            "& .MuiInputLabel-root": {
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                color: darkUi ? "var(--oc-text-muted)" : "#222",
                                "&.Mui-focused": {
                                    color: darkUi ? "var(--oc-text) !important" : "#222 !important",
                                },
                            },
                        }}
                        InputProps={{
                            sx: {
                                "&:focus-within": {
                                    backgroundColor: darkUi ? "var(--oc-paper-bg)" : "#FFFFFF",
                                    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                                    borderRadius: "4px",
                                },
                            },
                            disableUnderline: true,
                            // endAdornment: (
                            //     <InputAdornment position="end">
                            //         <InfoOutlinedIcon fontSize="small" />
                            //     </InputAdornment>
                            // ),
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t("confirmationNoInputLabel")}
                        variant="filled"
                        value={confirmationNumber}
                        onChange={(e) => setConfirmationNumber(e.target.value)}
                        sx={{
                            "& .MuiFilledInput-root": {
                                borderRadius: "4px",
                                boxShadow: darkUi ? "0 4px 8px rgba(0,0,0,0.35)" : "0px 4px 6px rgba(0,0,0,0.12)",
                                backgroundColor: darkUi ? "var(--oc-paper-bg)" : "rgba(0,0,0,0.06)",
                            },
                            "& .MuiFilledInput-input": { color: "var(--oc-text)" },
                            "& .MuiInputLabel-root": {
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                                color: darkUi ? "var(--oc-text-muted)" : "#222",
                                "&.Mui-focused": {
                                    color: darkUi ? "var(--oc-text) !important" : "#222 !important",
                                },
                            },
                        }}
                        InputProps={{
                            sx: {
                                "&:focus-within": {
                                    backgroundColor: darkUi ? "var(--oc-paper-bg)" : "#FFFFFF",
                                    boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                                    borderRadius: "4px",
                                },
                            },
                            disableUnderline: true,
                            // endAdornment: (
                            //     <InputAdornment position="end">
                            //         <InfoOutlinedIcon fontSize="small" />
                            //     </InputAdornment>
                            // ),
                        }}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                fullWidth
                sx={{
                    backgroundColor: BrandColors.accent,
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    py: 2.5,
                    px: 4,
                    maxWidth: 300,
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { backgroundColor: BrandColors.accent },
                }}
                onClick={() => handleStartCheckIn()}
                disabled={!lastName || !confirmationNumber || loading}
                endIcon={loading ? <CircularProgress size={24} color="inherit" /> : <ArrowForwardIcon sx={{ fontSize: "30px" }} />}
            >
                {t("startCheckInButtonLabel")}
            </Button>

            {/* <Typography
                variant="caption"
                onClick={handlePrivacyPolicyClick}
                sx={{
                    position: "absolute",
                    bottom: 60,
                    left: "50%",
                    transform: "translateX(-50%)",
                    color: "#888",
                    textDecoration: "underline",
                    cursor: "pointer",
                }}
            >
                {t("privacyPolicy")}
            </Typography> */}

            {/* 🧱 Hide language selector on mobile */}
            {/* {!isMobile && (
                <Grid
                    container
                    justifyContent="center"
                    spacing={1}
                    sx={{
                        position: "absolute",
                        bottom: -5,
                        left: "50%",
                        transform: "translateX(-50%)",
                    }}
                >
                    {[...SUPPORTED_LOCALES].map((lang) => (
                        <Grid item key={lang}>
                            <Button
                                onClick={() => handleLanguageChange({ target: { value: lang } })}
                                sx={{
                                    backgroundColor: i18n.language === lang ? "#7A5C3D" : "transparent",
                                    color: i18n.language === lang ? "#FFFFFF" : "#000",
                                    fontWeight: "bold",
                                    width: "150px",
                                    height: "56px",
                                    borderRadius: "4px",
                                    textTransform: "uppercase",
                                    borderBottom: i18n.language !== lang ? "3px solid #7A5C3D" : "none",
                                }}
                            >
                                {languageNames[lang]}
                            </Button>
                        </Grid>
                    ))}
                </Grid>
            )} */}
        </Box>
    );
};

export default CheckInPage;
